import { useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Tab, TextField, CircularProgress, Button,
} from '@mui/material';
import TabList from '@mui/lab/TabList';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { useSnackbar } from 'notistack';
import useStyles from '../styles';
import useFetchApi from '../../../hooks/useFetchApi';
import { GetSectorEt0ConfigResponse } from '../../../requests/api/apiTypes';
import { FetchError } from '../../../views/types';
import apiClient from '../../../requests/api/apiClient';
import { ET0_NAME } from '../../../constants/graphs';
import ConversationsTab from '../ConversationTab';
import { ModalState, EMPTY_MODAL_STATE } from '../chartModal';
import useIsClientOperator from '../../../hooks/useIsClientOperator';

const Et0ConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
}: {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  // Default to the conversations tab when arriving from a notification (date param).
  const date = queryParams.get('date');
  const initialTabValue = date ? '3' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);
  const [isLoading, setIsLoading] = useState(true);
  const [accSinceDate, setAccSinceDate] = useState('');

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const isClientOperator = useIsClientOperator();

  useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const { enqueueSnackbar } = useSnackbar();

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAccSinceDate(event.target.value);
  };

  const handleSetAccumulateFrom = async () => {
    try {
      const { data: response } = await apiClient.et0Config.et0ConfigCreate(
        sectorId,
        { accumulateFrom: accSinceDate },
      );
      enqueueSnackbar(response.message, { variant: 'success' });
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const { data, loading } = useFetchApi<GetSectorEt0ConfigResponse>({
    modelEndpoint: 'et0ConfigDetail',
    modelName: 'et0Config',
    id: sectorId,
    dependencies: [sectorId],
  });

  useEffect(() => {
    if (!loading) {
      setAccSinceDate(data?.et0Config?.accumulateFrom || new Date().toISOString().split('T')[0]);
      setIsLoading(false);
    }
  }, [data, loading]);

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <TabContext value={tabValue}>
      <Box sx={{ borderBottom: 0 }}>
        <TabList onChange={handleTabChange} aria-label="sector tabs">
          {!isClientOperator && (
            <Tab label="Configuración" value="1" className={classes.tabLabel} />
          )}
          <Tab label="Alertas" value="2" className={classes.tabLabel} />
          <Tab label="Conversaciones" value="3" className={classes.tabLabel} />
        </TabList>
      </Box>
      {!isClientOperator && (
        <TabPanel value="1" className={classes.configTabPanel}>
          <Card className={classes.configCard}>
            <CardContent>
              <Box className={classes.selectFieldsContainer}>
                <TextField
                  className={classes.selectField}
                  label="Acumular desde"
                  type="date"
                  value={accSinceDate}
                  onChange={handleDateChange}
                  fullWidth
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSetAccumulateFrom}
                  sx={{ ml: 2, mt: 2 }}
                >
                  Establecer
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      )}
      <TabPanel value="2">
        In progress
      </TabPanel>
      <TabPanel value="3">
        <ConversationsTab
          setModalState={setModalState}
          sectorId={sectorId}
          chartName={ET0_NAME}
          refreshCounter={refreshCounter}
        />
      </TabPanel>
    </TabContext>
  );
};

export default Et0ConfigContent;
